import { camelCase, cloneDeep, isEmpty, snakeCase } from 'lodash'

import { isKubernetes as isTargetTypeKubernetes } from 'components/attacks/create/targets/target-utils'
import { reformatSteps } from 'components/scenarios/utils/step-actions'
import {
  type ConcurrentNode,
  type ContinuousStatusCheckNode,
  type DelayNode,
  type FailureFlagNode,
  type GraphNode,
  type GraphNodeList,
  type InfraAttackNode,
  type Scenario,
  type ScenarioGraph,
  ScenarioGraphNodeType,
  ScenarioLifecycle,
  type ScenarioSource,
  type ScenarioStep,
  type StatusCheckReference,
  type SynchronousStatusCheckNode
} from 'types/api/scenarios/scenario'
import { isLastIndex, naturalSortBy } from 'utils/list'
import { cleanObjKeysAndValues, isObject, keysToCamelCase } from 'utils/obj'

import type {
  ScenarioRunGraph,
  ScenarioRunResponse
} from 'types/api/scenarios/runs/scenario-runs'

// limitation of the backend (dynamo)
export const MAX_GRAPH_NODES = 99

export const concurrentGraphTypes = [
  ScenarioGraphNodeType.Concurrent,
  ScenarioGraphNodeType.ContinuousStatusCheck
]
export const synchronousGraphTypes = [
  ScenarioGraphNodeType.InfraAttack,
  ScenarioGraphNodeType.FailureFlag,
  ScenarioGraphNodeType.Delay,
  ScenarioGraphNodeType.SynchronousStatusCheck,
  ScenarioGraphNodeType.FailureFlag
]

// this is intentionally vague to allow the type guard functions below to accept various node values
// and validate that they are the correct type of node  without throwing TS errors at their call point
export type PossibleGraphNode = Record<string, any> | null

export function isGraphBranch(node: PossibleGraphNode) {
  return node?.hasOwnProperty('start_id')
}

export function isGraphNode(node: PossibleGraphNode): node is GraphNode {
  return (
    node != null &&
    node.hasOwnProperty('type') &&
    Object.values(ScenarioGraphNodeType).includes(node.type)
  )
}

export const isGraphNodeType = (
  node: PossibleGraphNode,
  type: ScenarioGraphNodeType
) => node?.type === type

export function isInfraAttackNode(
  node: PossibleGraphNode | undefined
): node is InfraAttackNode {
  return (
    node !== undefined &&
    isGraphNodeType(node, ScenarioGraphNodeType.InfraAttack)
  )
}

export function isFailureFlagNode(
  node: PossibleGraphNode | undefined
): node is FailureFlagNode {
  return (
    node !== undefined &&
    isGraphNodeType(node, ScenarioGraphNodeType.FailureFlag)
  )
}

export function isDelayNode(node: PossibleGraphNode): node is DelayNode {
  return isGraphNodeType(node, ScenarioGraphNodeType.Delay)
}

export function isSynchronousStatusCheckNode(
  node: PossibleGraphNode | undefined
): node is SynchronousStatusCheckNode {
  return (
    node !== undefined &&
    isGraphNodeType(node, ScenarioGraphNodeType.SynchronousStatusCheck)
  )
}

export function isConcurrentNode(
  node: PossibleGraphNode
): node is ConcurrentNode {
  return isGraphNodeType(node, ScenarioGraphNodeType.Concurrent)
}

export function isChildGraphNode(node: PossibleGraphNode): boolean {
  return (
    isInfraAttackNode(node) ||
    isSynchronousStatusCheckNode(node) ||
    isDelayNode(node) ||
    isConcurrentNode(node)
  )
}

export function isContinuousStatusCheckNode(
  node: PossibleGraphNode
): node is ContinuousStatusCheckNode {
  return isGraphNodeType(node, ScenarioGraphNodeType.ContinuousStatusCheck)
}

export function isScenario(
  scenario: ScenarioSource | undefined
): scenario is Scenario {
  return scenario !== undefined && 'guid' in scenario
}

export const createDelayNode = (changes = {}) => ({
  type: ScenarioGraphNodeType.Delay,
  delay: 5,
  ...changes
})

export const walkGraph = function* (graph, start = graph?.start_id) {
  if (!start) return []

  let next = start
  while (next && graph.nodes[next]) {
    const current = graph.nodes[next]
    next = current?.next
    yield current
  }
}

/**
 * As opposed to the _generator_ function `walkGraph`, this will loop over all nodes of graph
 * (including the branches of concurrent nodes) until the conditionFunc
 * returns false. The idea is to prevent walking the full graph if it isn't necessary to do so.
 */
export function walkGraphUntilCondition(
  graph: ScenarioRunGraph | ScenarioGraph,
  conditionFunc: (node: GraphNode) => boolean
) {
  const { nodes, start_id } = graph
  const startNode = nodes[start_id]

  if (!startNode) return

  let next = start_id
  let current: GraphNode | undefined = startNode

  while (next && current && conditionFunc(current)) {
    if (isConcurrentNode(current)) {
      current.branches.forEach((branch) =>
        walkGraphUntilCondition(branch, conditionFunc)
      )
    }
    next = current?.next ?? ''
    current = next ? nodes[next] : undefined
  }
}

export const scenarioHasStatusCheck = (graph) => {
  return reformatSteps(graph).some(
    (node) =>
      isSynchronousStatusCheckNode(node) || isContinuousStatusCheckNode(node)
  )
}

export const hasAttackSteps = (scenario) =>
  getGraphNodesByType(scenario.graph, ScenarioGraphNodeType.InfraAttack)
    .length > 0

export const hasFailureFlagSteps = (scenario: Scenario) =>
  getGraphNodesByType(scenario.graph, ScenarioGraphNodeType.FailureFlag)
    .length > 0

export const getNodeAtIndex = (graph, index) => {
  // This is a simpler but more expensive implementation.
  // If we have have very large scenarios we could just walk the graph
  // rather than converting it to an array.
  return arrayFromGraph(graph)[index]
}

export const getNodeById = (graph, id) => graph.nodes[id]

export const arrayFromGraph = (graph) => {
  const arrayFromWalkGraph = Array.from(walkGraph(graph)).map((node) => {
    if (isConcurrentNode(node)) {
      return {
        ...node,
        branches: node.branches.map(arrayFromGraph)
      }
    }
    return node
  })

  if (isDelayNode(arrayFromWalkGraph[arrayFromWalkGraph.length - 1])) {
    arrayFromWalkGraph.splice(-1, 1)
  }
  return arrayFromWalkGraph
}

export const mapBranchedSteps = (steps, parent) =>
  steps.flatMap((step) => {
    if (step?.branches?.length) {
      return [
        step,
        ...step.branches.flatMap((branch, index) => {
          return mapBranchedSteps(branch, {
            ...step,
            branchId: `${step.id}-${index}` // use the step id as well as the index of the branch
          })
        })
      ]
    }
    if (parent) {
      return {
        ...step,
        branchId: parent.branchId,
        parent
      }
    }

    return step
  })

export const graphFromArray = (nodeArray) => {
  return nodeArray.reduce(
    (list, node, index) => {
      const isLast = isLastIndex(nodeArray, index)

      list.nodes[index] = {
        ...node,
        id: String(index)
      }

      delete list.nodes[index].next

      if (!isLast) {
        list.nodes[index].next = String(index + 1)
      }

      return list
    },
    { nodes: {}, start_id: '0' }
  )
}

export const concurrentGraphFromArray = (nodeArray) => ({
  start_id: 'concurrentNode',
  nodes: {
    concurrentNode: {
      id: 'concurrentNode',
      type: ScenarioGraphNodeType.Concurrent,
      branches: nodeArray
        .filter((node) => !isEmpty(node))
        .map((node) => graphFromArray(node))
    }
  }
})

export const lengthOfScenarioGraph = (graph) => Object.keys(graph.nodes).length

export const getLastScenarioGraphNode = (graph) =>
  Object.values(graph.nodes).find((node) => !node.next)

export const getGraphNodesByType = (graph, ...types) => {
  const typeSet = new Set(types)
  return mapBranchedSteps(arrayFromGraph(graph)).filter((node) =>
    typeSet.has(node.type)
  )
}

export const getGraphObservers = (graph) => {
  return mapBranchedSteps(graph?.observers ?? []).filter(
    (node) => node.type === ScenarioGraphNodeType.ContinuousStatusCheck
  )
}

export const getNextNodeOfType = (graph, start, ...types) => {
  const sequence = walkGraph(graph, start)
  const typeSet = new Set(types)
  let done = false
  while (!done) {
    const current = sequence.next()
    done = current.done
    if (current.value && typeSet.has(current.value.type)) {
      return current.value
    }
  }
}

// This covers the cases for retrieving the infraCommandArgs from the passed in attackConfiguration
// TODO: fix the key casing for the graph nodes
function getInfraCommandArgs(node) {
  if (node.attack_configuration) {
    return (
      node.attack_configuration.command.infraCommandArgs ||
      node.attack_configuration.command.infra_command_args
    )
  }

  if (node.attackConfiguration) {
    return (
      node.attackConfiguration.command.infraCommandArgs ||
      node.attackConfiguration.command.infra_command_args
    )
  }

  return node.impact_definition?.infra_command_args
}

function isServiceAttackNode(node) {
  return Boolean(
    keysToCamelCase(node).attackConfiguration?.targets[0]?.serviceId
  )
}

export function graphNodeToStepAttack(node: PossibleGraphNode) {
  if (
    isFailureFlagNode(node) ||
    isSynchronousStatusCheckNode(node) ||
    isContinuousStatusCheckNode(node) ||
    isConcurrentNode(node) ||
    isDelayNode(node)
  ) {
    return node
  }

  const nodeAttackConfiguration = node?.attack_configuration
  const hasTargetDefinition = Boolean(node?.target_definition)
  const hasImpactDefinition = Boolean(node?.impact_definition)

  const nodeTarget = node?.target_definition ?? {}
  const nodeImpact = node?.impact_definition ?? {}

  // Since Shared and Recommended scenarios have no targets in the attackConfiguration, we still need to default to
  // targetDefinition to extract the targetType
  const targetType =
    nodeAttackConfiguration?.targets[0]?.targetingStrategy?.type ||
    nodeTarget.target_type

  const isKubernetes = isTargetTypeKubernetes(targetType)
  const infraCommandArgs = getInfraCommandArgs(node)

  // Network tags are stored in an object where the key is the tag with an array of values.
  // Casing (camelCase/snake_case) matters for these tags, so we only want to transform the path to them
  // example: trafficImpactMapping.multiSelectTags
  const trafficImpactMapping = infraCommandArgs.traffic_impact_mapping
    ? cleanObjKeysAndValues(infraCommandArgs.traffic_impact_mapping, {
        keyTransformers: [camelCase]
      })
    : undefined

  const targetDefinition = {
    targetType,
    strategyType: hasTargetDefinition ? nodeTarget.strategy_type : 'Random',
    strategy: hasTargetDefinition
      ? cleanObjKeysAndValues(
          {
            ...nodeTarget.strategy,
            ...nodeTarget.strategy.attrs
          },
          {
            keysToRemove: ['attrs', 'type']
          }
        )
      : nodeAttackConfiguration.sampling
  }

  const impactDefinition = {
    commandType: nodeAttackConfiguration.command?.infraCommandType,
    commandArgs: hasImpactDefinition
      ? {
          ...cleanObjKeysAndValues(nodeImpact.infra_command_args, {
            keysToRemove: ['type', 'traffic_impact_mapping'],
            keyTransformers: [camelCase]
          }),
          trafficImpactMapping
        }
      : {
          ...nodeAttackConfiguration.command?.infraCommandArgs
        }
  }

  if (isKubernetes && !isServiceAttackNode(node)) {
    targetDefinition.strategy.k8sObjects = (nodeTarget?.k8s_objects ?? []).map(
      (target) =>
        cleanObjKeysAndValues(target, {
          keyTransformers: [camelCase]
        })
    )
    // TODO: do you want containerSelection or container_selection ?
    impactDefinition.containerSelection = nodeTarget?.containerSelection ?? {
      selectionType: 'ANY'
    }
  }

  return {
    attackType: 'ILFI',
    attackConfiguration: nodeAttackConfiguration,
    targetDefinition,
    impactDefinition,
    ...(node &&
      'include_new_targets' in node && {
        includeNewTargets: node.include_new_targets
      }),
    ...(node &&
      'continue_on_empty_targets' in node && {
        continueOnEmptyTargets: node.continue_on_empty_targets
      })
  }
}

export function graphToStepsAPI(graph: ScenarioGraph): ScenarioStep[] {
  const nodes = arrayFromGraph(graph)
  const steps: ScenarioStep[] = []

  for (let i = 0; i < nodes.length; ++i) {
    const node = nodes[i]

    if (isDelayNode(node)) {
      continue
    }

    const step: ScenarioStep = {
      attacks: [graphNodeToStepAttack(node)]
    }

    const nextNode = nodes[i + 1]

    if (nextNode && isDelayNode(nextNode)) {
      step.delay = nextNode.delay
    }

    steps.push(step)
  }

  return steps
}

export function stepAttackToGraphNode(step) {
  const attack = step.attacks[0]
  const attackConfiguration = attack.attackConfiguration
  const attackTarget = attack.targetDefinition
  const attackImpact = attack.impactDefinition
  const isKubernetes = isTargetTypeKubernetes(attackTarget.targetType)

  const targetStrategy = {
    attrs: cleanObjKeysAndValues(attackTarget.strategy, {
      keysToRemove: [
        'percentage',
        'count',
        'allHosts',
        'allContainers',
        'k8sObjects'
      ]
    })
  }

  if (attackTarget.strategy.allHosts) {
    targetStrategy.all_hosts = true
  } else if (attackTarget.strategy.allContainers) {
    targetStrategy.all_containers = true
  }

  if (attackTarget.strategyType === 'Random') {
    if (attackTarget.strategy.percentage) {
      targetStrategy.type = 'RandomPercent'
      targetStrategy.percentage = attackTarget.strategy.percentage
    } else {
      targetStrategy.type = 'RandomCount'
      targetStrategy.count = attackTarget.strategy.count
    }
  } else {
    targetStrategy.type = 'Exact'
  }

  // Network tags are stored in an object where the key is the tag with an array of values.
  // Casing (camelCase/snake_case) matters for these tags, so we only want to transform the path to them
  // example: trafficImpactMapping.multiSelectTags
  const traffic_impact_mapping = attackImpact.commandArgs.trafficImpactMapping
    ? cleanObjKeysAndValues(attackImpact.commandArgs.trafficImpactMapping, {
        keyTransformers: [snakeCase]
      })
    : undefined

  const node = {
    attack_configuration: attackConfiguration,
    target_definition: {
      strategy_type: attackTarget.strategyType,
      target_type: attackTarget.targetType,
      strategy: targetStrategy
    },
    impact_definition: {
      infra_command_type: attackImpact.commandType,
      infra_command_args: {
        ...cleanObjKeysAndValues(attackImpact.commandArgs, {
          keysToRemove: ['commandType', 'trafficImpactMapping'],
          keyTransformers: [snakeCase]
        }),
        type: attackImpact.commandType
      }
    },
    type: ScenarioGraphNodeType.InfraAttack,
    id: step.id
  }

  if (traffic_impact_mapping) {
    node.impact_definition.infra_command_args.traffic_impact_mapping =
      traffic_impact_mapping
  }

  if (isKubernetes) {
    node.target_definition.k8s_objects = (
      attack?.targetDefinition?.strategy?.k8sObjects ?? []
    ).map((target) =>
      cleanObjKeysAndValues(target, {
        keyTransformers: [snakeCase]
      })
    )
  }
  return node
}

export function stepsToGraphAPI(steps) {
  const graph = {}
  const nodeArray = steps.reduce((stepNodes, step, index) => {
    const isLastStep = isLastIndex(steps, index)

    const node = stepAttackToGraphNode(step)

    stepNodes.push(node)

    if (!isLastStep && step.delay) {
      stepNodes.push({
        delay: step.delay,
        id: (index + 1).toString(),
        type: ScenarioGraphNodeType.Delay
      })
    }

    return stepNodes
  }, [])

  const nodes = nodeArray.map((node, index) => {
    const n = {
      ...node,
      id: `${index}`
    }
    if (!isLastIndex(nodeArray, index)) {
      n.next = `${index + 1}`
    }

    return [index, n]
  })

  graph.nodes = Object.fromEntries(nodes)
  graph.start_id = '0'

  return graph
}

// allows us to recursively strip parent from steps
export const stripParentFromSteps = (steps) => {
  const newSteps = [...steps]

  newSteps.forEach((step) => {
    delete step.parent
    if (step.branches) {
      step.branches = stripParentFromSteps(step.branches)
    }
    return step
  })

  return newSteps
}

// we do this both when adding/editing Steps and when saving scenario
// TODO: Concurrent Attacks Project: Convert to mapping Concurrent nodes instead of dividing between continuous status check and synchronous graphs
export const formatRequestBodyGraph = (steps) => {
  const nodeGraph = graphFromArray(stripParentFromSteps(steps))
  let graph

  const continuousStatusChecksGraph = getGraphNodesByType(
    nodeGraph,
    ScenarioGraphNodeType.ContinuousStatusCheck
  ).map((node) => [node])

  const synchronousGraph = getGraphNodesByType(
    nodeGraph,
    ...synchronousGraphTypes
  )

  if (continuousStatusChecksGraph.length > 0) {
    graph = concurrentGraphFromArray([
      ...continuousStatusChecksGraph,
      synchronousGraph
    ])
  } else {
    // If there are no continuous status checks,
    // there are no concurrent nodes. Remove parent parameter.
    synchronousGraph.map((step) => {
      delete step.branchId
    })
    graph = graphFromArray(synchronousGraph)
  }

  return graph
}

export const getIndentLevel = (step, previousStep) => {
  const currentBranchId = step?.branchId
  const previousBranchId = previousStep?.branchId

  const hasSameBranchId =
    currentBranchId && previousBranchId && currentBranchId === previousBranchId

  const shouldIndent =
    // indent if previous step is a continuous status check node or they share the same branch
    (isContinuousStatusCheckNode(previousStep) || hasSameBranchId) &&
    // and if the current step isn't a status check node
    !isContinuousStatusCheckNode(step)

  return shouldIndent ? 1 : 0
}

export const getInteractiveNodes = (steps) =>
  steps.filter((step) => !isDelayNode(step) && !isConcurrentNode(step))

// TODO rename to node?
export const findRunStep = (
  run: ScenarioRunResponse,
  currentStep: string,
  preferredDefault?: GraphNode | FailureFlagNode
) => {
  return run && currentStep
    ? // @ts-expect-error type mismatch
      (reformatSteps(run.graph)
        .concat(run.graph.observers ?? [])
        .find((currRun) => getStepId(currRun) === currentStep) as GraphNode)
    : preferredDefault ?? undefined
}

export const getStepId = (step) =>
  step?.guid ?? `${step?.branchId ?? ''}${step?.id}`

// Concurrent Graph Utilities
export type GraphNodePath = [
  'nodes' | 'branches' | 'observers',
  ...Array<number | string>
]

export function getObserverPath(observerNumber: number): GraphNodePath {
  return ['observers', observerNumber]
}

export type GraphNodePathMap = {
  [key: string]: GraphNodePath
}

/**
 * Generates a map of Scenario graph node guids to their path in the graph
 * This is a way for us to be able to quickly walk the graph and grab their node
 */
export function generateGraphNodePathMap(
  graph: ScenarioGraph | ScenarioRunGraph
): GraphNodePathMap {
  const observerPaths = {}

  if (graph?.observers && graph?.observers?.length > 0) {
    graph.observers.forEach(
      (o, n) => (observerPaths[o.guid] = getObserverPath(n))
    )
  }

  return {
    ...observerPaths,
    ...generateRecursiveGraphNodePaths(graph?.nodes, ['nodes'])
  }
}

/**
 *  Recursively handles following graph node children and building up their node path maps
 */
export function generateRecursiveGraphNodePaths(
  nodes: GraphNodeList,
  parentPath: GraphNodePath
) {
  let nodePaths = {}

  Object.entries(nodes ?? {}).forEach(([key, node]) => {
    switch (node.type) {
      case ScenarioGraphNodeType.Concurrent:
        nodePaths[node.guid] = [...parentPath, key]
        node.branches.forEach((branch, branchIndex) => {
          // there's no guid for branches so just add their node tree
          nodePaths = {
            ...nodePaths,
            ...generateRecursiveGraphNodePaths(branch.nodes, [
              ...parentPath,
              key,
              'branches',
              branchIndex,
              'nodes'
            ])
          }
        })
        break

      default:
        nodePaths[node.guid] = [...parentPath, key]
    }
  })

  return nodePaths
}

export type GraphNodeAtPath =
  | GraphNode
  | GraphNodeList
  | ScenarioGraph[]
  | ScenarioRunGraph[]
  | ScenarioGraph
  | ScenarioRunGraph
  | StatusCheckReference
  | null

/**
 * Follow the supplied GraphPath to extract a node from a ScenarioGraph
 */
export function getNodeAtPath(
  graph: ScenarioGraph,
  path?: GraphNodePath
): GraphNodeAtPath {
  if (!path || path.length === 0) {
    return null
  }

  // short circuit to avoid infinite loop in parent path retrieval
  if (path.length === 1 && path[0] === 'nodes') {
    return graph.nodes as unknown as ScenarioGraph
  }

  let node = graph

  path.forEach((pathPart) => {
    node = node[pathPart]
  })

  // nodes in the graph are of various types
  // so we whittle down the exact type here

  // this is an actual graph node (attack, status check, delay, concurrent node)
  if (isGraphNode(node)) {
    return node as unknown as GraphNode
  }

  // branch list
  if (Array.isArray(node)) {
    return node as unknown as ScenarioGraph[]
  }

  if (isObject(node)) {
    // an individual branch is a ScenarioGraph
    if (isGraphBranch(getParentGraphNode(graph, getNodeParentPath(path)))) {
      return node as unknown as ScenarioGraph
    }

    // otherwise and object is a list of nodes
    return node as unknown as GraphNodeList
  }

  return node
}

/**
 * Get the node from the graph by its node path, but return it typed as an InfraAttackNode
 * This is used as a type guard since getNodeAtPath can return various types
 */
export function getGraphNodeAtPathAsInfraAttack(
  graph: ScenarioGraph,
  path?: GraphNodePath
): InfraAttackNode {
  const node = getNodeAtPath(graph, path)

  if (!isInfraAttackNode(node)) {
    return {} as InfraAttackNode
  }

  return node as InfraAttackNode
}

export function getNodeParentPath(path: GraphNodePath): GraphNodePath {
  return path.slice(0, -1) as GraphNodePath
}

function replaceTopLevelGraphNodeAtPath(
  graph: ScenarioGraph,
  node: GraphNodeAtPath,
  nodePath: GraphNodePath
) {
  const newNodeKey = String(nodePath[1])

  if (newNodeKey) {
    if (nodePath[0] === 'observers' && isContinuousStatusCheckNode(node)) {
      if (!graph.observers) {
        graph.observers = [node]
      } else {
        graph.observers[newNodeKey] = node
      }
    } else if (nodePath[0] === 'nodes' && isGraphNode(node)) {
      if (!graph.nodes) {
        graph.nodes = { [newNodeKey]: node }
      } else {
        graph.nodes[newNodeKey] = node
      }
    }
  }

  return graph
}

export function replaceGraphNodeAtPath(
  graph: ScenarioGraph,
  node: GraphNodeAtPath,
  nodePath: GraphNodePath
): ScenarioGraph {
  if (!node) {
    return graph
  }

  // short circuit top level replacement
  if (nodePath.length === 1) {
    graph[nodePath[0]] = node
    return graph
  }

  // short circuit top-level graph paths that have no parent
  if (nodePath.length < 3) {
    return replaceTopLevelGraphNodeAtPath(graph, node, nodePath)
  }

  const parentPath = getNodeParentPath(nodePath)
  const parent = getNodeAtPath(graph, parentPath)

  if (parent) {
    const key = String(nodePath.at(-1))

    if (key) {
      parent[key] = node
    }
  }

  return graph
}

function deleteTopLevelGraphNodeAtPath(
  graph: ScenarioGraph,
  nodePath: GraphNodePath
) {
  const nodeKey = String(nodePath[1])

  if (nodeKey) {
    if (nodePath[0] === 'observers') {
      graph.observers?.splice(Number(nodeKey), 1)
    } else if (nodePath[0] === 'nodes') {
      delete graph.nodes[nodeKey]
    }
  }

  return graph
}

/**
 * Delete a node from the graph at the supplied path
 */
export function deleteGraphNodeAtPath(
  graph: ScenarioGraph,
  nodePath: GraphNodePath
): ScenarioGraph {
  // short circuit top-level graph paths that have no parent
  if (nodePath.length < 3) {
    return deleteTopLevelGraphNodeAtPath(graph, nodePath)
  }

  const parentPath = getNodeParentPath(nodePath)
  const parent = getNodeAtPath(graph, parentPath)

  if (parent) {
    const key = String(nodePath.at(-1))

    if (key) {
      if (Array.isArray(parent)) {
        parent.splice(Number(key), 1)
      } else {
        delete parent[key]
      }
    }
  }

  return graph
}

/**
 * Generate a minimal string representation of a node path for use in the `node.id` field
 * This was requested by the backend team to make debug messages that use the id easier to read
 */
export function serializeNodePathToId(path: GraphNodePath) {
  return path
    .join('-')
    .replaceAll('nodes-', 'n')
    .replaceAll('branches-', 'b')
    .replaceAll('observers-', 'o')
}

/**
 * Convert serialize node path id string back into a path array
 * (this likely won't ever be needed in webapp
 */
export function deserializeNodeIdToPath(nodeId: string): GraphNodePath {
  const idString = nodeId ?? ''
  return idString
    .replaceAll(/b(\d+)/g, 'branches-$1')
    .replaceAll(/o(\d+)/g, 'observers-$1')
    .replaceAll(/n(\d+)/g, 'nodes-$1')
    .split('-') as GraphNodePath
}

/**
 * Typing here is explicitly vague because it can accept either a branch or a node
 */
export function getParentGraphNode(
  graph: ScenarioGraph,
  nodePath: GraphNodePath
) {
  let parentPath = getNodeParentPath(nodePath)
  let parentNode = getNodeAtPath(graph, parentPath)

  while (!isGraphNode(parentNode)) {
    parentPath = getNodeParentPath(parentPath)
    parentNode = getNodeAtPath(graph, parentPath)
  }

  return parentNode
}

export function findNearestAttackNode(
  graph: ScenarioGraph,
  nodePath: GraphNodePath
) {
  // grab the map for actual node paths
  const graphNodePathMap = generateGraphNodePathMap(graph)

  const attackNodes = Object.entries(graphNodePathMap)
    // filter out anything that's not an attack node
    .filter(([, path]) => {
      const node = getNodeAtPath(graph, path)
      return isInfraAttackNode(node)
    })
    // pull in the actual node, and ensure it has a valid `id`
    .map(([, path]) => {
      const node: GraphNode = getNodeAtPath(graph, path) as GraphNode
      node.id = serializeNodePathToId(path)
      return node
    })

  // no attack nodes?
  if (attackNodes.length < 1) {
    return null
  }

  // if there's only one, return it
  if (nodePath.length === 1) {
    return attackNodes[0]
  }

  // ensure the list is sorted by id (which should give us the graph order from the top down)
  // so whatever we return is the nearest attack above the target in the graph
  const sortedAttackNodes = naturalSortBy(attackNodes, 'id', 'asc')

  // now find the nearest one
  // let's compare the ids to find the nearest one
  const targetNodeId = serializeNodePathToId(nodePath)
  const targetNodeIdParts = targetNodeId.split('-')

  // compare the node ids to generate a "distance"
  // split the target node id into a list of its parts
  // then compare those to the corresponding parts in each attack node
  // the attack node with the smallest distance (fewer differences in path) is the nearest
  const nodesWidthIdDistance = sortedAttackNodes.map((node) => {
    const nodeIdParts = node.id.split('-')
    let distance = 0
    for (let i = 0; i < targetNodeIdParts.length; i++) {
      if (nodeIdParts[i] !== targetNodeIdParts[i]) {
        distance++
      }
    }

    return {
      node,
      distance
    }
  })

  // re-sort the list and return the first attack
  const sortedNodes = naturalSortBy(nodesWidthIdDistance, 'distance', 'asc')
  return sortedNodes[0].node
}

export function connectAndReorderBranchNodes(branch: ScenarioGraph) {
  branch.start_id = '0'

  // convert to list we can sort by key
  const branchEntries = Object.entries(branch.nodes).map(([key, node]) => ({
    key,
    node
  }))
  const numNodes = branchEntries.length

  if (numNodes > 0) {
    const lastNode = Number(numNodes - 1)
    // sort by key
    const sortedBranchEntries = naturalSortBy(branchEntries, 'key', 'asc')
    // re-map to object using zero-based index as key
    const branchNodes = sortedBranchEntries.map(({ node }, nodeNum) => {
      // ensure that child nodes are all re-ordered too
      if (isConcurrentNode(node)) {
        node.branches.forEach(connectAndReorderBranchNodes)
      }

      // while looping over the branch nodes, reset their `next` prop to point to the next node
      if (nodeNum < lastNode) {
        node.next = String(nodeNum + 1)
      } else {
        delete node.next
      }

      return [nodeNum, node]
    })
    // and re-assmble into an object
    branch.nodes = Object.fromEntries(branchNodes)
  }

  // note: we don't automatically remove empty branches here when we encounter them
  // and instead if the user clicks save they'll get an error from the backend
  // explaining what's wrong with the scenario graph they've created
  // if we automatically remove it, then they may think they created a scenario structured
  // as they intended, and the result will be different
  // this way we ensure that what they create is exactly what they expect
}

export function isFirstGraphNode(nodeKey: string) {
  return nodeKey === '0' || nodeKey === 'concurrentNode'
}

/**
 * Return the number of nodes in a graph
 *
 * Generates a GraphNodePathMap because that returns only the actual nodes
 */
export function getGraphNodeCount(graph: ScenarioGraph) {
  const graphNodePathMap = generateGraphNodePathMap(graph)
  return Number(Object.keys(graphNodePathMap).length)
}

/**
 * Check if the graph exceeds the total limit for graph nodes
 */
export function doesGraphHaveTooManyNodes(graph: ScenarioGraph) {
  return getGraphNodeCount(graph) > MAX_GRAPH_NODES
}

/**
 * Return the number of nested levels in a graph path
 *
 * We have a limit on the number of nested levels in a graph, so we have to check that we don't exceed that
 * Using the GraphNodePath means we don't have to deal with walking the graph structure
 * We can just count the number of branches in the path
 */
export function getGraphPathNestingLevel(path: GraphNodePath) {
  const numBranchesInPath = path.filter((p) => p === 'branches').length
  // nesting level is the # of branches
  return numBranchesInPath
}

export function getOrderedNodesFromBranch(
  branch: ScenarioGraph | ScenarioRunGraph
) {
  const orderedNodes = []
  let nextNode = branch.nodes[branch.start_id]

  while (nextNode) {
    orderedNodes.push([nextNode.id, nextNode])
    nextNode = branch.nodes[nextNode.next]
  }

  return orderedNodes
}

/**
 * Converts a ScenarioGraph we received from the API into a format that we can use easily in the UI
 *
 * This re-keys the `nodes` objects to be ordinal integers and ensures that the `id` property of the object matches its key
 * This way we can use simpler ordinal logic when modifying the graph in the UI,
 * and GraphNodePaths will be simpler to utilize as well
 *
 * Without this step, there would be significant effort required to modify the UI code in order to support
 * using the ids from serializeNodePathToId() as the `id` property and object keys.
 * Traversing the graph tree, as well as altering it, would be FAR more complex (I know, I tried it - G)
 */
export function recursivelyReorderGraphNodesAsOrdinalKeys(
  branch: ScenarioGraph | ScenarioRunGraph
) {
  const orderedNodes = []
  let nextNode = branch.nodes[branch.start_id]

  while (nextNode) {
    const nodeId = orderedNodes.length

    if (isConcurrentNode(nextNode)) {
      nextNode.branches = nextNode.branches.map(
        recursivelyReorderGraphNodesAsOrdinalKeys
      )
    }

    orderedNodes.push({
      // copy the rest of the node
      ...nextNode,
      // update the id
      id: nodeId,
      // connect to the next node (if there is one) with its new id
      next: nextNode.next ? nodeId + 1 : undefined
    })

    nextNode = branch.nodes[nextNode.next]
  }

  branch.start_id = '0'
  branch.nodes = Object.fromEntries(orderedNodes.map((node) => [node.id, node]))

  return branch
}

/**
 * Convert the ScenarioGraph we receive from the API into the format we'll operate on in the UI
 */
export function unpackScenarioGraph(graph: ScenarioGraph | ScenarioRunGraph) {
  // make a copy so we don't alter the original graph
  const unpackedGraph = cloneDeep(graph)

  // convert `nodes` object keys to ordinal integers and ensure that `id` property of the object matches its key
  // and its `next` property is updated to point to the next ordinal key
  return recursivelyReorderGraphNodesAsOrdinalKeys(unpackedGraph)
}

/**
 * Reverse the conversion we did when we read in the ScenarioGraph from the API
 * Turn the ordinal node keys back into serialized node path IDs
 */
export function recursivelyReorderGraphNodesAsSerializedIds(
  branch: ScenarioGraph,
  graphNodePathMap: GraphNodePathMap
) {
  const orderedNodes = []
  let node = branch.nodes[branch.start_id]
  let startId = ''

  while (node) {
    const nodePath = graphNodePathMap[node.guid]
    const nodeId = serializeNodePathToId(nodePath)

    // keep track of the updated start_id so we can update it in the graph when we're done
    if (!startId) {
      startId = nodeId
    }

    if (isConcurrentNode(node)) {
      node.branches = node.branches.map((branch) => {
        return recursivelyReorderGraphNodesAsSerializedIds(
          branch,
          graphNodePathMap
        )
      })
    }

    const newNode = {
      // copy the rest of the node
      ...node,
      // update the id
      id: nodeId,
      next: undefined
    }

    if (node.next) {
      const nextNode = branch.nodes[node.next]
      const nextNodePath: GraphNodePath = graphNodePathMap[nextNode.guid]
      const nextNodeId = serializeNodePathToId(nextNodePath)
      // connect  the next node (if there is one) with its new id
      newNode.next = nextNodeId
    }

    orderedNodes.push(newNode)
    node = branch.nodes[node.next]
  }

  branch.start_id = startId
  branch.nodes = Object.fromEntries(orderedNodes.map((node) => [node.id, node]))

  return branch
}

/**
 * Convert our UI representation of the ScenarioGraph back into the format that the API expects
 */
export function repackScenarioGraph(graph: ScenarioGraph) {
  const graphNodePathMap = generateGraphNodePathMap(graph)
  const repackedGraph = structuredClone(graph)

  // convert nodes object keys and id/next properties back to serialized node path IDs
  return recursivelyReorderGraphNodesAsSerializedIds(
    repackedGraph,
    graphNodePathMap
  )
}

/**
 * Return the non-branch nodes that match the condition set in `matchFunc` (or all if not provided).
 */
export function getMatchingGraphNodes(
  nodes?: GraphNodeList,
  matchingNodes: GraphNode[] = [],
  matchFunc?: (node: GraphNode) => boolean
) {
  Object.values(nodes ?? {}).forEach((node) => {
    if (node.type === ScenarioGraphNodeType.Concurrent) {
      node.branches.forEach((branch) => {
        return getMatchingGraphNodes(branch.nodes, matchingNodes, matchFunc)
      })
    } else if (!matchFunc || matchFunc(node)) {
      matchingNodes.push(node)
    }
  })

  return matchingNodes
}

/**
 * Recursively extract the currently active InfraAttack nodes
 */
export function getActiveGraphNodes(nodes?: GraphNodeList) {
  return getMatchingGraphNodes(
    nodes,
    [],
    (node) => node.state?.lifecycle === ScenarioLifecycle.Active
  )
}
